import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clientFetch } from "../utils/clientFetch";
import { UpdateAuthContext } from "./authContext";

type UserType = {
  email: string
  department_name: string
  is_admin: boolean
}

const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_STATUS_FORBIDDEN = 403

const defaultUser: UserType = {
  email: '',
  department_name: '',
  is_admin: false
}

export const UserContext = createContext<{
  user: UserType
  isAuth: boolean | undefined
  isGuest: boolean
  // 管理者の読み取り専用モードを示すフラグ。
  // null = 未確定（安全側に倒して編集不可として扱う）
  // true = 管理者が他部署のサンプルを閲覧中（読み取り専用）
  // false = 通常モード（編集可能）
  // ライフサイクル:
  //   1. refreshUser() でユーザーの is_admin をもとに初期値をセット
  //   2. fetchUrlQrSample() / fetchQrCode() でサンプルごとの is_admin_mode に上書き
  isAdminMode: boolean | null
}>({
  user: defaultUser,
  isAuth: undefined,
  isGuest: false,
  isAdminMode: null
})

export const UpdateUserContext = createContext<{
  refreshUser: () => Promise<void>
  setIsAuth: (bol: boolean) => void
  setIsGuest: (bol: boolean) => void
  setIsAdminMode: (bol: boolean | null) => void
}>({
  refreshUser: async () => { },
  setIsAuth: (bol: boolean) => { },
  setIsGuest: (bol: boolean) => { },
  setIsAdminMode: (bol: boolean | null) => { }
})

export function useUserContext() {
  const [user, setUser] = useState({ email: '', department_name: '', is_admin: false })
  const [isAuth, setIsAuth] = useState<boolean | undefined>(undefined)
  const [isGuest, setIsGuest] = useState<boolean>(false)
  // 管理者の読み取り専用モード（詳細は UserContext の型定義コメントを参照）
  const [isAdminMode, setIsAdminMode] = useState<boolean | null>(null)
  const { logout } = useContext(UpdateAuthContext)
  const refreshUser = useCallback(async () => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/user`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: false
      })
      if (res.status === HTTP_STATUS_UNAUTHORIZED) {
        setUser({ email: 'ゲスト', department_name: '', is_admin: false })
        setIsAuth(false)
        setIsGuest(false)
        setIsAdminMode(null)
        return
      }
      if (res.status === HTTP_STATUS_FORBIDDEN) {
        setUser(defaultUser)
        setIsAuth(false)
        setIsGuest(false)
        setIsAdminMode(null)
        await logout()
        return
      }

      const data = await res.json();
      setUser({
        email: data?.email ?? '',
        department_name: data?.department_name ?? '',
        is_admin: data?.is_admin === true
      })
      setIsAuth(true)
      // 初期値としてユーザーの is_admin をセット（管理者なら true）。
      // サンプル取得時に fetchUrlQrSample() 等が実際の is_admin_mode で上書きする。
      setIsAdminMode(data?.is_admin === true)
    } catch (error) {
      console.error("Error fetching user.");
      setIsAuth(false)
      setIsGuest(false)
      setIsAdminMode(null)
    }
  }, [logout])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  return {
    user,
    isAuth,
    isGuest,
    isAdminMode,
    refreshUser,
    setIsAuth,
    setIsGuest,
    setIsAdminMode
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuth, isGuest, isAdminMode, refreshUser, setIsAuth, setIsGuest, setIsAdminMode } = useUserContext()

  return (
    <UpdateUserContext.Provider value={{ refreshUser, setIsAuth, setIsGuest, setIsAdminMode }}>
      <UserContext.Provider value={{ user, isAuth, isGuest, isAdminMode }}>
        {children}
      </UserContext.Provider>
    </UpdateUserContext.Provider>
  )
}
